import { Component, type ErrorInfo, type ReactNode } from "react";
import { ErrorBanner } from "@/components/ui";
import { reportCrash } from "@/utils/telemetry";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (reset: () => void, error: Error) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (typeof console !== "undefined") {
      console.error("[ErrorBoundary]", error, info.componentStack);
    }
    reportCrash(error, { componentStack: info.componentStack });
  }

  reset = (): void => this.setState({ error: null });

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.reset, error);
    return (
      <div className="p-8 max-w-[640px] mx-auto">
        <ErrorBanner
          title="Esta vista no pudo cargarse"
          message={error.message || "Ocurrió un error inesperado."}
          onRetry={this.reset}
        />
      </div>
    );
  }
}
